import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Box,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { TFunction } from 'i18next';

import { StructuredOutputBanner } from '../components/banners/StructuredOutputBanner';
import CampaignSection from '../components/CampaignSection';
import BreadcrumbsNavigation from '../components/minicomponents/BreadcrumbsNavigation';
import { InfoTooltipView } from '../components/minicomponents/InfoTooltipView';
import SectionButton from '../components/minicomponents/SectionButton';
import {
  IconDelete,
  IconEditText,
  IconError,
  IconLock,
  IconMoreVert,
  IconPremiumAccess,
} from '../components/minicomponents/icons';
import AlertPopup from '../components/popups/AlertPopup';
import ConfirmationPopup from '../components/popups/ConfirmationPopup';
import InputPopup from '../components/popups/InputPopup';
import RegisterPopup from '../components/popups/RegisterPopup';
import UpgradePopup from '../components/popups/UpgradePopup';
import { getCategoryFromPathname } from '../utils/analytics/navigation';
import { trackSentryError } from '../utils/analytics/sentry';
import { trackClickSection } from '../utils/analytics/analytics';
import {
  finalizeCampaignCreation,
  handleApiError as handleApiErrorUtil,
} from '../utils/api';
import { registerAsync } from '../utils/auth';
import { colors } from '../utils/colors';
import { LOCKED_SECTIONS_PER_PLAN, SECTIONS } from '../utils/const';
import {
  deleteCampaign,
  updateCampaign,
  useListenCampaignChanges,
  useListenProductChanges,
} from '../utils/db';
import useBreakpoint, { useFirebaseLoginState } from '../utils/hooks';
import { setSelectedCampaign, setSelectedProduct } from '../utils/state';
import { RootState } from '../utils/store';
import { parseDomainFromUrl } from '../utils/textUtils';
import { getTranslationKey } from '../utils/translationUtils';
import {
  CampaignSection as CampaignSectionType,
  Item,
  LoadingStatus,
  PlanCode,
  ScreenWidth,
  SectionName,
} from '../utils/types';

const campaignNameLengthFormatted = (
  campaignName: string,
  currentBreakpoint: ScreenWidth
) => {
  const breakpoints = {
    desktopWide: 100,
    desktop: 80,
    laptop: 60,
    tablet: 25,
    mobile: 20,
  };
  const maxLength =
    breakpoints[currentBreakpoint as keyof typeof breakpoints] || breakpoints.mobile;
  return campaignName.length > maxLength
    ? campaignName.substring(0, maxLength) + '...'
    : campaignName;
};

const createBreadcrumbItems = (
  productUrl: string,
  campaignName: string,
  currentBreakpoint: ScreenWidth,
  t: TFunction
) => {
  const breadcrumbItems = [
    {
      name: t('app.screens.campaigns.title'),
      path: '/app/campaigns',
    },
    {
      name: (
        <Typography
          variant="h2"
          color="text.primary"
          sx={{ display: 'flex', alignItems: 'baseline' }}
        >
          {parseDomainFromUrl(productUrl) && (
            <>
              <Typography component="span" fontSize={20} fontWeight={700}>
                {parseDomainFromUrl(productUrl)}
              </Typography>
              <Typography
                component="span"
                fontSize={20}
                color={colors.text.secondary}
                sx={{ mx: 1 }}
              >
                |
              </Typography>
            </>
          )}
          <Typography component="span" fontSize={20}>
            {campaignNameLengthFormatted(campaignName, currentBreakpoint)}
          </Typography>
        </Typography>
      ),
      path: '',
    },
  ];
  return breadcrumbItems;
};

function isSectionLocked(
  section: CampaignSectionType,
  userPlan?: PlanCode,
  isUserMigratedToCredits?: boolean
): boolean {
  if (userPlan === 'not_registered') {
    return LOCKED_SECTIONS_PER_PLAN[userPlan].includes(section.name);
  } else if (isUserMigratedToCredits) {
    return false;
  } else if (!userPlan) {
    return true;
  } else {
    return LOCKED_SECTIONS_PER_PLAN[userPlan].includes(section.name);
  }
}

function buildSectionStatusIcon(
  section: CampaignSectionType,
  isLoading: boolean,
  isFailed: boolean,
  t: TFunction,
  userPlan?: PlanCode,
  isUserMigratedToCredits?: boolean,
  isRegistered?: boolean
): React.ReactElement {
  if (isFailed)
    return <IconError sx={{ color: 'error.main', fontSize: 20, display: 'flex' }} />;
  if (isLoading)
    return (
      <CircularProgress size={16} sx={{ display: 'flex', color: 'text.secondary' }} />
    );
  if (isSectionLocked(section, userPlan, isUserMigratedToCredits)) {
    return (
      <InfoTooltipView
        icon={
          !isRegistered ? (
            <IconLock
              sx={{
                color: colors.text.secondary,
                fontSize: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          ) : (
            <IconPremiumAccess
              sx={{
                color: colors.text.secondary,
                fontSize: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          )
        }
        isEventPropagationEnabled={true}
        text={
          !isRegistered
            ? t('website.googleAuthButton.signInInvitation')
            : t('website.upgradePopup.higherPlan.hint')
        }
      />
    );
  }
  return <></>;
}

export const CampaignScreen = () => {
  // Redux/Router hooks
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const state = useSelector((state: RootState) => state.appState);

  const userPlan = state?.subcription?.planCode;
  const isUserMigratedToCredits = !!state?.flags?.isUserMigratedToCredits;

  // URL/Auth state
  const campaignId = location.pathname.split('/').pop();
  const { isRegistered, userId } = useFirebaseLoginState();
  const currentBreakpoint: ScreenWidth = useBreakpoint();

  // Data fetching hooks
  const { campaign, isUnknownResourceError } = useListenCampaignChanges(campaignId);
  const { product } = useListenProductChanges(campaign?.product_id);

  // Local state hooks
  const [anchorMenuEl, setAnchorMenuEl] = useState<null | HTMLElement>(null);
  const [inputDialogOpen, setInputDialogOpen] = useState(false);
  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [isUpgradePopupOpen, setUpgradePopupOpen] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionName>(SectionName.Summary);
  const [isRegisterPopupOpen, setIsRegisterPopupOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finalizationTriggered, setFinalizationTriggered] = useState(false);

  // Derived state
  const isMenuOpen = Boolean(anchorMenuEl);

  // Error handling callback
  const handleError = useCallback((error: unknown) => {
    handleApiErrorUtil({
      error,
      onSetUpgradePopupOpen: setUpgradePopupOpen,
      onSetErrorMessage: setError,
      onSetErrorAlertOpen: (isOpen) => {
        setIsAlertOpen(isOpen);
        if (!isOpen) setError(null);
      },
    });
  }, []);

  const handleTryNow = useCallback(() => {
    setActiveSection(SectionName.SMM);
  }, []);

  // Effects
  useEffect(() => {
    let redirectTimeout: NodeJS.Timeout;
    if (isUnknownResourceError) {
      redirectTimeout = setTimeout(() => {
        navigate(`/app/campaigns${location.search}`);
      }, 1000); // Redirect after 1 second
    }
    return () => {
      clearTimeout(redirectTimeout);
    };
  }, [isUnknownResourceError, navigate]);

  useEffect(() => {
    if (campaign) {
      dispatch(setSelectedCampaign(campaign));
    }
  }, [campaign, dispatch]);

  useEffect(() => {
    if (product) {
      dispatch(setSelectedProduct(product));
    }
  }, [product, dispatch]);

  useEffect(() => {
    const finalizeCampaignIfNeeded = async () => {
      if (campaign && campaign.id && userId && !finalizationTriggered) {
        setFinalizationTriggered(true);
        try {
          await finalizeCampaignCreation(userId, campaign.id, state.session);
        } catch (error) {
          trackSentryError('Error finalizing campaign', {
            userId,
            campaignId: campaign.id,
            error,
          });
          console.error('Error finalizing campaign:', error);
        }
      }
    };

    finalizeCampaignIfNeeded();
  }, [campaign, userId, state.session, finalizationTriggered]);

  const handleSectionClick = useCallback(
    (sectionName: SectionName, isEnabled: boolean, isRegistered: boolean) => {
      // Track section click
      trackClickSection(sectionName);

      if (isEnabled) {
        setActiveSection(sectionName);
      } else {
        if (isRegistered) {
          setUpgradePopupOpen(true);
        } else {
          setIsRegisterPopupOpen(true);
        }
      }
    },
    []
  );

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorMenuEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorMenuEl(null);
  };

  const handleRenameClick = () => {
    setInputDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteConfirmationOpen(true);
    handleMenuClose();
  };

  const handleRenameSubmit = async (newName: string) => {
    if (newName && campaign) {
      try {
        await updateCampaign(campaign.id!, { name: newName });
      } catch (error) {
        trackSentryError('Error renaming campaign', {
          campaignId: campaign.id,
          newName,
          error,
        });
        console.error('Error renaming campaign:', error);
      }
    }
    setInputDialogOpen(false);
  };

  const handleRenameDialogClose = () => {
    setInputDialogOpen(false);
  };

  const handleDeleteConfirmationSubmit = async () => {
    if (campaign) {
      try {
        await deleteCampaign(campaign.id!);
        navigate(`/app/campaigns${location.search}`);
      } catch (error) {
        trackSentryError('Error deleting campaign', { campaignId: campaign.id, error });
        console.error('Error deleting campaign:', error);
      }
    }
    setDeleteConfirmationOpen(false);
  };

  const isSectionItemsLoading = (items?: Item[]): boolean => {
    return items?.some((item) => item.status === LoadingStatus.LOADING) || false;
  };

  const isSectionItemsFailed = (items?: Item[]): boolean => {
    return items?.some((item) => item.status === LoadingStatus.FAILED) || false;
  };

  const getCampaignSectionItems = (sectionName?: SectionName): Item[] => {
    if (sectionName && campaign) {
      const section = SECTIONS.find((section) => section.name === sectionName);
      if (!section) return [];

      const sectionItems = section.tabs?.flatMap((tab) => tab.items) || [];
      const tabItems = section.tabs?.flatMap((tab) => tab.items) || [];
      const allItems = [...sectionItems, ...tabItems];

      return campaign.items?.filter((item) => allItems.includes(item.type)) || [];
    }
    return [];
  };

  if (!campaign) return <></>;

  const handleRegisterPopupClose = () => {
    setIsRegisterPopupOpen(false);
  };

  const handleRegisterClicked = async () => {
    setIsRegisterPopupOpen(false);
    try {
      const category = getCategoryFromPathname(location.pathname);
      await registerAsync(category);
      // Note: User will be redirected to Google auth
      // Success handling happens in App.tsx when user returns from redirect
    } catch (error) {
      trackSentryError('Registration error', { error });
      console.error('Registration error:', error);
      // Show error to user - they will remain on the campaign screen
    }
  };

  return (
    <Container maxWidth="desktop" sx={{ mt: 4, mb: 4 }}>
      <Grid container gap={2}>
        {/* Breadcrumbs and menu */}
        <Grid container size={12} alignItems="center">
          <Grid size="grow">
            <BreadcrumbsNavigation
              items={createBreadcrumbItems(
                product?.url || '',
                campaign?.name || '',
                currentBreakpoint,
                t
              )}
            />
          </Grid>
          <Grid>
            <IconButton
              aria-label="more"
              aria-controls="long-menu"
              aria-haspopup="true"
              onClick={handleMenuClick}
              sx={{ color: 'text.primary' }}
            >
              <IconMoreVert />
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={anchorMenuEl}
              keepMounted
              open={isMenuOpen}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleRenameClick}>
                <ListItemIcon>
                  <IconEditText />
                </ListItemIcon>
                {t('app.buttons.rename')}
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleDeleteClick}>
                <ListItemIcon>
                  <IconDelete />
                </ListItemIcon>
                {t('app.buttons.delete')}
              </MenuItem>
            </Menu>
          </Grid>
        </Grid>

        <Grid size={12}>
          <StructuredOutputBanner onTryNow={handleTryNow} />
        </Grid>

        {/* Sidebar with sections */}
        <Grid
          container
          direction="row"
          size={12}
          sx={{
            bgcolor: 'background.default',
            borderRadius: '8px',
          }}
        >
          <Grid sx={{ p: 2, pr: 0 }}>
            {SECTIONS.map((section) => (
              <Box key={section.name} maxWidth={'300px'}>
                <SectionButton
                  icon={section.icon}
                  name={t(`app.sectionsNames.${getTranslationKey(section.name)}`)}
                  isActive={activeSection === section.name}
                  rightIcon={buildSectionStatusIcon(
                    section,
                    isSectionItemsLoading(getCampaignSectionItems(section.name)),
                    isSectionItemsFailed(getCampaignSectionItems(section.name)),
                    t,
                    userPlan,
                    isUserMigratedToCredits,
                    isRegistered
                  )}
                  onClick={() =>
                    handleSectionClick(
                      section.name,
                      !isSectionLocked(section, userPlan, isUserMigratedToCredits),
                      isRegistered
                    )
                  }
                />
              </Box>
            ))}
          </Grid>

          {/* Main section content */}
          <Grid container size="grow" sx={{ p: 3 }}>
            <CampaignSection key={activeSection} sectionName={activeSection} />
          </Grid>
        </Grid>
      </Grid>

      {/* InputPopup for renaming campaign */}
      <InputPopup
        open={inputDialogOpen}
        onClose={handleRenameDialogClose}
        onSubmit={handleRenameSubmit}
        buttonText={t('app.buttons.rename')}
        dialogTitle={t('app.popups.rename.title')}
        dialogText={t('app.popups.rename.text')}
        inputValue={campaign?.name || ''}
      />

      {/* ConfirmationPopup for delete confirmation */}
      <ConfirmationPopup
        open={isDeleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        onSubmit={handleDeleteConfirmationSubmit}
        confirmButtonText={t('app.buttons.delete')}
        message={t('app.popups.delete.text')}
        title={t('app.popups.delete.title')}
        disclaimer={t('app.popups.disclaimers.cannotBeUndone')}
        confirmButtonColor="error.main"
      />

      {/* UpgradePopupDialog */}
      <UpgradePopup
        open={isUpgradePopupOpen}
        onClose={() => setUpgradePopupOpen(false)}
        onError={handleError}
        title={isRegistered ? t('website.upgradePopup.higherPlan.title') : undefined}
        header={isRegistered ? t('website.upgradePopup.higherPlan.header') : undefined}
        featuresTitle={
          isRegistered ? t('website.upgradePopup.higherPlan.features.title') : undefined
        }
        features={
          isRegistered
            ? [
                t('website.upgradePopup.higherPlan.features.feature1'),
                t('website.upgradePopup.higherPlan.features.feature2'),
                t('website.upgradePopup.higherPlan.features.feature3'),
                t('website.upgradePopup.higherPlan.features.feature4'),
              ]
            : undefined
        }
        footer={isRegistered ? t('website.upgradePopup.footer') : undefined}
      />

      {/* RegisterPopupDialog */}
      <RegisterPopup
        open={isRegisterPopupOpen}
        onClose={handleRegisterPopupClose}
        onRegisterClicked={handleRegisterClicked}
      />

      <AlertPopup
        open={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onSubmit={() => setIsAlertOpen(false)}
        alertText={error || ''}
        title={t('app.popups.error.title')}
        buttonText={t('app.buttons.ok')}
      />
    </Container>
  );
};
